import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';

// Services
import { NavigationParam, RouteParam, ServiceBase } from '../service-base';

// Modèles
import { GeneralFactory } from 'src/app/@shared/models/factories/general-factory';
import { Quotation } from 'src/app/@shared/models/sales/quotation/quotation.model';
import { QuotationItem } from 'src/app/@shared/models/sales/quotation/quotation-item.model';
import { File as FileModel } from 'src/app/@shared/models/file/file.model';

const CLASS_NAME = 'Quotation';
const ENDPOINT = `/quotations/`;
const NAVIGATIONS: NavigationParam[] = [
    { key: 'downpayments', path: 'downpayments/', childClass: 'SalesInvoice' },
    { key: 'invoices', path: 'invoices/', childClass: 'SalesInvoice' },
    { key: 'logo', path: 'logo/', childClass: 'File', resultCardinality: 'one' },
]
const ROUTES: RouteParam[] = [
    { key: 'accept', path: '{id}/accept/' },
    { key: 'addendum', path: '{id}/addendum/' },
    { key: 'cancel', path: '{id}/cancel/' },
    { key: 'close', path: '{id}/close/' },
    { key: 'createVersion', path: '{id}/version/' },
    { key: 'downloadFile', path: '{id}/pdf/' },
    { key: 'duplicate', path: '{id}/duplicate/' },
    { key: 'export', path: 'export/' },
    { key: 'markAsSent', path: '{id}/mark-as-sent/' },
    { key: 'massDelete', path: 'mass-delete/' },
    { key: 'massDownload', path: 'mass-download/' },
    { key: 'materialCorrection', path: '{id}/material-correction/' },
    { key: 'refuse', path: '{id}/refuse/' },
    { key: 'remind', path: '{id}/remind/' },
    { key: 'reopen', path: '{id}/reopen/' },
    { key: 'send', path: '{id}/send/' },
    { key: 'validate', path: '{id}/validate/' },
]

@Injectable({
    providedIn: 'root'
})
export class QuotationService extends ServiceBase {
    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addNavigations(NAVIGATIONS);
        this.addRoutes(ROUTES);
    }

    public accept$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.accept.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    /**
     * Correction matérielle d'une ligne du contrat (#1001) : une coquille de libellé
     * ou de description s'applique directement, sans produire d'avenant.
     *
     * ⚠️ Toujours appelé sur le devis **racine**, même quand la ligne vit sur un
     * avenant effectif — c'est un geste de contrat. Le payload est **strict** : toute
     * clé hors `productName` / `productDescription` est refusée par le serveur.
     *
     * @param quotationId Le devis **racine** (jamais l'avenant depuis lequel on édite)
     * @param itemId La ligne du **contrat** à corriger (`contractItemId` d'une ligne
     *               de brouillon, pas l'id de la copie)
     * @param fields Le libellé et/ou la description corrigés (au moins un des deux)
     * @returns La ligne corrigée, ou `null` si le serveur a refusé (le message est
     *          déjà affiché par le helper) — l'appelant ne doit alors rien enchaîner.
     */
    public applyMaterialCorrection$(quotationId: number, itemId: number, fields: { productName?: string, productDescription?: string }): Observable<QuotationItem | null> {
        const url = this.routes['materialCorrection'].replace('{id}', quotationId.toString());
        return this._postActionValue$<any>(url, { itemId: itemId, ...fields }).pipe(
            map(value => !!value ? GeneralFactory.get('QuotationItem', value) as QuotationItem : null)
        );
    }

    public cancel$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.cancel.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public close$(quotation: Quotation): Observable<Quotation> {
        const url = this.routes['close'].replace('{id}', quotation.id.toString());
        return this._putAction$<Quotation>(url).pipe(
            tap(instance => this._updated$.next(instance))
        );
    }

    /**
     * Ouverture du brouillon d'avenant du devis signé (#1001) : **sans payload**.
     * Le serveur crée le brouillon pré-rempli du restatement du contrat en vigueur
     * (`201`), ou renvoie le brouillon déjà en cours (`200`) — dans les deux cas
     * l'appelant navigue vers l'avenant retourné, qui s'édite ensuite avec le
     * formulaire de devis standard.
     *
     * @param quotation Le devis racine
     */
    public createAddendum$(quotation: Quotation): Observable<Quotation> {
        const url = this.routes['addendum'].replace('{id}', quotation.id.toString());
        return this._postAction$<Quotation>(url).pipe(
            tap(instance => this._updated$.next(instance))
        );
    }

    public createVersion$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.createVersion.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public duplicate$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.duplicate.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public getLogo$(quotation: any): Observable<FileModel> {
        return this.getRelated$(quotation, 'logo');
    }

    public markAsSent$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.markAsSent.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public refuse$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.refuse.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public remind$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.remind.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public reopen$(quotation: Quotation): Observable<Quotation> {
        const url = this.routes['reopen'].replace('{id}', quotation.id.toString());
        return this._putAction$<Quotation>(url).pipe(
            tap(instance => this._updated$.next(instance))
        );
    }

    public send$(quotation: Quotation): Observable<Quotation> {
        let url = this.routes.send.replace('{id}', quotation.id.toString());
        return this.update$(quotation, {
            url: url
        });
    }

    public uploadLogo$(quotation: any, file: File): Observable<any> {
        let url = this.routes['single'].replace('{id}', quotation.id.toString()) + this.navigations['logo'].path;
        return this.uploadFile$(file, url);
    }

}
